import { mapGroupBy } from '@plato/shared-util/map';
import { CustomerIcon } from '@plato/ui-legacy/assets/icons/customer-icon';
import { Text } from '@plato/ui-legacy/components/ui/text';
import type React from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
    type ActivityFeedRecord,
    ActivityFeedModelType,
} from '../../../server/types/customer-activity-feed-types.ts';
import type { TaskCompletedSource } from '../../analytics/types/analytics-events.ts';
import { TimelineList } from '../../shared/components/timeline-list/timeline-list.tsx';
import { ActivityFeedCustomerStatusChangeItem } from './activity-feed-customer-status-change-item.tsx';
import { ActivityFeedInsightOutcomeItem } from './activity-feed-insight-outcome-item.tsx';
import { ActivityFeedNoteItem } from './activity-feed-note-item.tsx';
import { ActivityFeedQuoteOrderItem } from './activity-feed-quote-order-item.tsx';
import { ActivityFeedTaskItem } from './activity-feed-task-item.tsx';

const ITEM_COMPONENTS = {
    TASK: ActivityFeedTaskItem,
    NOTE: ActivityFeedNoteItem,
    ORDER: ActivityFeedQuoteOrderItem,
    QUOTE: ActivityFeedQuoteOrderItem,
    INSIGHT_OUTCOME: ActivityFeedInsightOutcomeItem,
    CUSTOMER_STATUS_CHANGE: ActivityFeedCustomerStatusChangeItem,
} as const;

type Props = {
    isLoading: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean | undefined;
    isFetchingNextPage: boolean;
    timeGroupedData: Map<string, ActivityFeedRecord[]>;
    /**
     * When true (global feed), activities within each day are sub-grouped by customer with a
     * customer-name link header. The customer-detail feed leaves this off (single customer).
     */
    groupByCustomer?: boolean;
    /** Which surface hosts this feed, so task completions are attributed to it rather than to the customer page. */
    source: TaskCompletedSource;
};

export function ActivityFeedTimeline({
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    timeGroupedData,
    groupByCustomer = false,
    source,
}: Props) {
    const [lastListItemRef] = useInView({
        threshold: 0.1,
        onChange: (inView) => {
            if (!isLoading && inView && hasNextPage) {
                fetchNextPage();
            }
        },
        skip: isLoading || isFetchingNextPage,
    });

    const lastArray = [...timeGroupedData.values()][timeGroupedData.size - 1];
    const lastItem = lastArray?.at(-1);

    const renderItem = (activity: ActivityFeedRecord): React.JSX.Element => {
        const isLastItem = activity.activity_id === lastItem?.activity_id;
        const ref = isLastItem ? lastListItemRef : undefined;

        if (activity.model_type === ActivityFeedModelType.TASK) {
            return (
                <ActivityFeedTaskItem
                    key={activity.activity_id}
                    activity={activity}
                    ref={ref}
                    source={source}
                />
            );
        }

        const Component = ITEM_COMPONENTS[activity.model_type];

        return <Component key={activity.activity_id} activity={activity} ref={ref} />;
    };

    return (
        <TimelineList isLoading={isLoading || isFetchingNextPage}>
            {Array.from(timeGroupedData, ([date, items]) => (
                <TimelineList.Group date={new Date(date)} key={`${date}-${items.length}`}>
                    {groupByCustomer
                        ? Array.from(
                              mapGroupBy(items, (item) => item.customer_id),
                              ([customerId, customerItems]) => (
                                  <div
                                      className="flex w-full flex-col gap-2 last:mb-0 mb-5"
                                      key={customerId || 'unknown'}
                                  >
                                      {customerId && (
                                          <Link
                                              to={`/customers/${encodeURIComponent(customerId)}`}
                                              className="flex w-fit items-center"
                                          >
                                              <div className="ml-1 mr-2 flex h-6 w-6 shrink-0 items-center justify-center">
                                                  <CustomerIcon className="h-4 w-4 text-blue-600" />
                                              </div>
                                              <div className="flex flex-col">
                                                  <Text
                                                      size="xs"
                                                      color="neutral-500"
                                                      className="tabular-nums"
                                                  >
                                                      {customerId}
                                                  </Text>
                                                  <Text size="sm" weight="medium" color="blue-600">
                                                      {customerItems[0]?.customer_name}
                                                  </Text>
                                              </div>
                                          </Link>
                                      )}
                                      {customerItems.map(renderItem)}
                                  </div>
                              ),
                          )
                        : items.map(renderItem)}
                </TimelineList.Group>
            ))}
        </TimelineList>
    );
}
